import { API_URL } from 'config';
import { makeAPICall } from './api';

import { ObjToURLSearchParams } from 'utils';

export declare namespace API {
    export interface SensorRecord {
        id?: number;
        value: number;
        time: string;
        field: string;
        sensor: number;
    }

    export interface SensorField {
        id: string;
        name: string;
        description: string;
        type: 'temperature' | 'humidity' | 'other';
        units: string;
        high_warning_alert: number;
        high_error_alert: number;
        low_warning_alert: number;
        low_error_alert: number;
        chart_max_value_range: number;
        chart_min_value_range: number;
        minimum_alert_time: number;
        minimum_alert_records: number;
        remove_after: number;
        created: string;
    }

    export interface SensorFieldConnection {
        id: number;
        connection: string;
        settings: Record<string, string | number | boolean>;
        created: string;
        field: string;
        sensor: number;
    }

    export interface Sensor {
        id: number;
        fields: SensorFieldConnection[];
        sensor_id: string;
        name: string;
        description: string;
        type: string;
        enabled: boolean;
        rate: number;
        config: Record<string, string | number | boolean>;
        created: string;
        ground_station: number;
    }
}

export interface SensorRecord {
    id?: number;
    value: number;
    time: string;
    field: string;
    sensor: number;
}

export interface SensorField {
    id: string;
    name: string;
    description: string;
    type: 'temperature' | 'humidity' | 'other';
    units: string;
    high_warning_alert: number;
    high_error_alert: number;
    low_warning_alert: number;
    low_error_alert: number;
    chart_max_value_range: number;
    chart_min_value_range: number;
    minimum_alert_time: number;
    minimum_alert_records: number;
    remove_after: number;
    created: Date;
}

export interface SensorFieldConnection {
    id: number;
    connection: string;
    settings: Record<string, string | number | boolean>;
    created: Date;
    field: string;
    sensor: number;
}

export interface Sensor {
    id: number;
    fields: SensorFieldConnection[];
    sensor_id: string;
    name: string;
    description: string;
    type: string;
    enabled: boolean;
    rate: number;
    config: Record<string, string | number | boolean>;
    created: Date;
    groundStation: number;
}

const SENSORS_ROUTE = '/sensors';
const SENSOR_FIELDS_ROUTE = '/sensor-fields';
const SENSOR_RECORDS_ROUTE = '/sensors-records';

export const mapAPIToSensorFieldConnection = ({
    created,
    ...rest
}: API.SensorFieldConnection): SensorFieldConnection => ({
    ...rest,
    created: new Date(created),
});

export const mapAPIToSensor = ({
    ground_station,
    created,
    fields,
    ...rest
}: API.Sensor): Sensor => ({
    ...rest,
    fields: fields.map(mapAPIToSensorFieldConnection),
    groundStation: ground_station,
    created: new Date(created),
});

export const mapAPIToSensorField = ({
    created,
    ...rest
}: API.SensorField): SensorField => ({
    ...rest,
    created: new Date(created),
});

export const mapAPIToSensorRecord = ({
    ...rest
}: API.SensorRecord): SensorRecord => ({
    ...rest,
});

export const listSensors = makeAPICall<
    Sensor[],
    { groundStationId: number },
    { results: API.Sensor[] }
>(
    ({ groundStationId }) => ({
        url: `${API_URL}${SENSORS_ROUTE}/?ground_station=${groundStationId}`,
    }),
    (data) => data.results.map(mapAPIToSensor),
);

export const listSensorFields = makeAPICall<
    SensorField[],
    void,
    { results: API.SensorField[] }
>(
    () => ({
        url: `${API_URL}${SENSOR_FIELDS_ROUTE}/`,
    }),
    (data) => data.results.map(mapAPIToSensorField),
);

export interface SensorRecordFilters {
    limit?: number;
    offset?: number;
    sensor__ground_station?: number;
    before_time?: string;
    after_time?: string;
    field?: string;
    sensor?: number;
    ordering?: 'time' | '-time' | 'value' | '-value';
}

export const getRecords = makeAPICall<
    SensorRecord[],
    SensorRecordFilters,
    { results: API.SensorRecord[] }
>(
    (filters) => ({
        url: `${API_URL}${SENSOR_RECORDS_ROUTE}/?${ObjToURLSearchParams(
            filters,
        ).toString()}`,
    }),
    (data) => data.results.map(mapAPIToSensorRecord),
);

export const getSensorById = makeAPICall<Sensor, { id: number }, API.Sensor>(
    ({ id }) => ({
        url: `${API_URL}${SENSORS_ROUTE}/${id}/`,
    }),
    (sensor) => mapAPIToSensor(sensor),
);
